import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewEncapsulation,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { getTailwindColorValue, ThemeColorName } from '@examdojo/styles';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { SliderComponent } from '@examdojo/core/widget';
import { TreeComponent } from '../../tree/tree/tree.component';
import { DEFAULT_URN_PARAMS, PositionedBall, UrnBallType, UrnParams } from './urn.model';
import { buildUrnTree, computeDrawColumns } from './urn-tree.model';
import {
  createDropSimulation,
  createGapFillSimulation,
  createRiseSimulation,
  createSettleSimulation,
  destroySimulation,
  isSettled,
  SettleSimulation,
  stepSimulation,
  stepSingleBallSimulation,
} from './urn-physics.util';
import { packBalls, simplifyFraction } from './urn.util';

@Component({
  selector: 'dojo-urn',
  standalone: true,
  imports: [SliderComponent, ButtonComponent, IconButtonComponent, TreeComponent, MatSlideToggle, MatTooltip],
  templateUrl: './urn.component.html',
  styleUrl: './urn.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class UrnComponent implements OnDestroy {
  constructor(private readonly cdr: ChangeDetectorRef) {
    effect(
      () => {
        const p = this.params();
        const max = p.maxCount ?? 20;
        const counts: Record<string, number> = {};
        for (const bt of p.ballTypes) {
          counts[bt.id] = Math.min(bt.initialCount, max);
        }
        this.ballCounts.set(counts);
        this.statementIndex.set(p.initialStatementIndex ?? 0);
        if (p.initialDrawCount !== null && p.initialDrawCount !== undefined) {
          this.drawCount.set(p.initialDrawCount);
        }
        if (p.initialWithReplacement !== null && p.initialWithReplacement !== undefined) {
          this.withReplacement.set(p.initialWithReplacement);
        }
      },
      { allowSignalWrites: true },
    );

    // Reset draw selections when drawCount changes
    effect(
      () => {
        const count = this.drawCount();
        this.drawSelections.set(Array.from({ length: count }, () => null));
      },
      { allowSignalWrites: true },
    );
  }

  private static nextId = 0;

  readonly params = input<UrnParams>(DEFAULT_URN_PARAMS);

  readonly uid = `urn-${UrnComponent.nextId++}`;

  readonly ballCounts = signal<Record<string, number>>({});
  readonly statementIndex = signal<number>(0);
  readonly chosenBall = signal<PositionedBall | null>(null);
  readonly isAnimating = signal<boolean>(false);
  readonly animatedBall = signal<{ type: UrnBallType; cx: number; cy: number; r: number } | null>(null);

  // ── Tree-related signals ──
  readonly drawCount = signal(1);
  readonly withReplacement = signal(true);
  readonly drawSelections = signal<Array<string | null>>([null]);

  private simulation: SettleSimulation | null = null;
  private concurrentSettleSim: SettleSimulation | null = null;
  private animationFrameId = 0;

  readonly minimal = computed(() => this.params().minimal ?? false);
  readonly maxCount = computed(() => this.params().maxCount ?? 20);
  readonly ballTypes = computed(() => this.params().ballTypes);

  readonly totalCount = computed(() => {
    const counts = this.ballCounts();
    return Object.values(counts).reduce((sum, n) => sum + n, 0);
  });

  /** Fixed per-type slider max (always maxCount). Total is enforced via clamping in onSliderChange. */
  readonly sliderMaxMap = computed(() => {
    const max = this.maxCount();
    const map: Record<string, number> = {};
    for (const bt of this.ballTypes()) {
      map[bt.id] = max;
    }
    return map;
  });

  readonly positionedBalls = computed(() => packBalls(this.ballCounts(), this.ballTypes()));

  /** When set, overrides positionedBalls for rendering (used during gravity settle). */
  readonly settledBalls = signal<PositionedBall[] | null>(null);

  /** The balls actually rendered — settledBalls if available, otherwise positionedBalls. */
  readonly displayBalls = computed(() => this.settledBalls() ?? this.positionedBalls());

  readonly chosenBallIndex = computed(() => {
    const chosen = this.chosenBall();
    if (!chosen) {
      return -1;
    }
    return this.displayBalls().indexOf(chosen);
  });

  readonly currentBallType = computed(() => {
    const types = this.ballTypes();
    const idx = this.statementIndex() % types.length;
    return types[idx];
  });

  readonly probability = computed(() => {
    const bt = this.currentBallType();
    const count = this.ballCounts()[bt.id] ?? 0;
    const total = this.totalCount();
    const simplified = total > 0 ? simplifyFraction(count, total) : { num: 0, den: 0 };
    const isSimplified = total > 0 && (simplified.num !== count || simplified.den !== total);
    return { count, total, simplified, isSimplified };
  });

  /** Resolved CSS color string per ball type id */
  readonly resolvedColorMap = computed(() => {
    const map: Record<string, string> = {};
    for (const bt of this.ballTypes()) {
      map[bt.id] = getTailwindColorValue(bt.color);
    }
    return map;
  });

  /** Resolved color for the current probability statement ball type */
  readonly currentBallTypeColor = computed(() => getTailwindColorValue(this.currentBallType().color));

  /** SVG fill string per ball type id (solid color or pattern url) */
  readonly ballFillMap = computed(() => {
    const colors = this.resolvedColorMap();
    const map: Record<string, string> = {};
    for (const bt of this.ballTypes()) {
      map[bt.id] = bt.pattern === 'solid' ? colors[bt.id] : `url(#${this.uid}-pat-${bt.id})`;
    }
    return map;
  });

  // ── Tree computed properties ──

  /** Index of the next empty draw slot, or -1 if all filled. */
  readonly currentDrawIndex = computed(() => this.drawSelections().findIndex((s) => s === null));

  /** Whether any draws have been made. */
  readonly hasDraws = computed(() => this.drawSelections().some((s) => s !== null && s !== undefined));

  /** Whether the DRAW BALL button should be enabled. */
  readonly canDraw = computed(() => this.currentDrawIndex() >= 0 && this.totalCount() > 0 && !this.isAnimating());

  readonly drawColumns = computed(() =>
    computeDrawColumns(
      this.drawCount(),
      this.withReplacement(),
      this.ballTypes(),
      this.ballCounts(),
      this.drawSelections(),
    ),
  );

  private readonly treeResult = computed(() =>
    buildUrnTree(this.drawCount(), this.withReplacement(), this.ballTypes(), this.ballCounts(), this.drawSelections(), {
      expandAll: this.minimal(),
      zoomEnabled: false,
    }),
  );

  readonly treeParams = computed(() => this.treeResult().tree);

  readonly selectedTreePath = computed(() => {
    const path = this.treeResult().selectedPath;
    return path.length > 0 ? [path] : [];
  });

  /** Per-depth colors for the selected tree path, matching each drawn ball's color. */
  readonly selectedTreePathColors = computed(() => {
    const selections = this.drawSelections();
    const types = this.ballTypes();
    const colors: ThemeColorName[] = [];
    for (const sel of selections) {
      if (sel === null || sel === undefined) {
        break;
      }
      const bt = types.find((t) => t.id === sel);
      colors.push(bt?.color ?? 'neutral');
    }
    return colors.length > 0 ? [colors] : [];
  });

  readonly treeHighlightColor: ThemeColorName = 'neutral';
  readonly maxDrawCount = 5;

  /** Icon SVG fill per ball type id */
  readonly iconFillMap = computed(() => {
    const colors = this.resolvedColorMap();
    const map: Record<string, string> = {};
    for (const bt of this.ballTypes()) {
      map[bt.id] = bt.pattern === 'solid' ? colors[bt.id] : `url(#${this.uid}-icon-${bt.id})`;
    }
    return map;
  });

  /** Darker stroke color per ball type (shade 700) for crisp flat edges */
  readonly ballStrokeMap = computed(() => {
    const map: Record<string, string> = {};
    for (const bt of this.ballTypes()) {
      map[bt.id] = getTailwindColorValue(bt.color, 700);
    }
    return map;
  });

  /** Darker stroke color for icon circles */
  readonly iconStrokeMap = computed(() => {
    const map: Record<string, string> = {};
    for (const bt of this.ballTypes()) {
      map[bt.id] = getTailwindColorValue(bt.color, 700);
    }
    return map;
  });

  // ── Public instance methods ──

  ngOnDestroy(): void {
    this.cleanupSimulation();
  }

  setDrawCount(n: number): void {
    if (n === this.drawCount()) {
      return;
    }
    this.restoreDrawnBalls();
    this.drawCount.set(n);
    this.settledBalls.set(null);
  }

  toggleReplacement(value: boolean): void {
    if (value === this.withReplacement()) {
      return;
    }
    this.restoreDrawnBalls();
    this.withReplacement.set(value);
    this.clearDrawSelections();
    this.settledBalls.set(null);
  }

  /** Reset draws: restore removed balls (if without replacement) and clear selections. */
  resetDraws(): void {
    this.restoreDrawnBalls();
    this.clearDrawSelections();
    this.settledBalls.set(null);
  }

  onSliderChange(ballTypeId: string, value: number): void {
    this.cleanupSimulation();
    this.settledBalls.set(null);
    this.isAnimating.set(false);
    this.clearDrawSelections();
    this.ballCounts.update((prev) => ({ ...prev, [ballTypeId]: value }));
  }

  clearJar(): void {
    this.cleanupSimulation();
    this.settledBalls.set(null);
    this.isAnimating.set(false);
    this.clearDrawSelections();
    const counts: Record<string, number> = {};
    for (const bt of this.ballTypes()) {
      counts[bt.id] = 0;
    }
    this.ballCounts.set(counts);
  }

  chooseBall(): void {
    const balls = this.displayBalls();
    const drawIdx = this.currentDrawIndex();
    if (balls.length === 0 || this.isAnimating() || drawIdx < 0) {
      return;
    }

    const randomIndex = Math.floor(Math.random() * balls.length);
    const ball = balls[randomIndex];
    this.chosenBall.set(ball);
    this.isAnimating.set(true);

    const otherBalls = balls.filter((b) => b !== ball);

    this.runRiseAnimation(ball, otherBalls, (finalPos, settledOtherBalls) => {
      this.recordDraw(drawIdx, ball.type.id);

      if (this.withReplacement()) {
        this.runDropAnimation(ball, finalPos.cx, finalPos.cy, settledOtherBalls, (dropPos) => {
          const allSettled = [...settledOtherBalls, { ...ball, cx: dropPos.cx, cy: dropPos.cy }];
          this.settledBalls.set(allSettled);
          this.animatedBall.set(null);
          this.chosenBall.set(null);
          this.isAnimating.set(false);
        });
      } else {
        this.ballCounts.update((prev) => ({
          ...prev,
          [ball.type.id]: Math.max(0, (prev[ball.type.id] ?? 0) - 1),
        }));
        this.animatedBall.set(null);
        this.chosenBall.set(null);
        this.runPhysicsSettle(settledOtherBalls);
      }
    });
  }

  trackBall(_index: number, ball: PositionedBall): string {
    return ball.id;
  }

  trackBallType(_index: number, bt: UrnBallType): string {
    return bt.id;
  }

  // ── Private instance methods ──

  private recordDraw(index: number, typeId: string): void {
    this.drawSelections.update((prev) => {
      const updated = [...prev];
      updated[index] = typeId;
      return updated;
    });
  }

  /** Restore ball counts for any balls drawn without replacement. */
  private restoreDrawnBalls(): void {
    if (this.withReplacement()) {
      return;
    }

    const selections = this.drawSelections();
    const hasDrawn = selections.some((s) => s !== null && s !== undefined);
    if (!hasDrawn) {
      return;
    }

    this.ballCounts.update((prev) => {
      const next = { ...prev };
      for (const sel of selections) {
        if (sel !== null && sel !== undefined) {
          next[sel] = (next[sel] ?? 0) + 1;
        }
      }
      return next;
    });
  }

  private clearDrawSelections(): void {
    this.drawSelections.set(Array.from({ length: this.drawCount() }, () => null));
  }

  private runRiseAnimation(
    ball: PositionedBall,
    otherBalls: PositionedBall[],
    onComplete: (finalPos: { cx: number; cy: number }, settledOtherBalls: PositionedBall[]) => void,
  ): void {
    this.cleanupSimulation();

    this.animatedBall.set({ type: ball.type, cx: ball.cx, cy: ball.cy, r: ball.r });
    this.settledBalls.set(otherBalls);

    const riseSim = createRiseSimulation(ball);
    this.simulation = riseSim;

    const settleSim = createGapFillSimulation(otherBalls);
    this.concurrentSettleSim = settleSim;

    let currentSettledBalls = otherBalls;
    let frame = 0;
    const maxFrames = 180;

    const step = () => {
      if (!this.simulation) {
        return;
      }

      const pos = stepSingleBallSimulation(riseSim);
      this.animatedBall.set({ type: ball.type, cx: pos.cx, cy: pos.cy, r: ball.r });

      if (this.concurrentSettleSim && !isSettled(this.concurrentSettleSim)) {
        currentSettledBalls = stepSimulation(this.concurrentSettleSim, otherBalls);
        this.settledBalls.set(currentSettledBalls);
      }

      this.cdr.detectChanges();
      frame++;

      if (pos.cy < 0 || frame >= maxFrames) {
        this.cleanupSimulation();
        onComplete({ cx: pos.cx, cy: pos.cy }, currentSettledBalls);
      } else {
        this.animationFrameId = requestAnimationFrame(step);
      }
    };

    this.animationFrameId = requestAnimationFrame(step);
  }

  private runDropAnimation(
    ball: PositionedBall,
    dropFromX: number,
    dropFromY: number,
    otherBalls: PositionedBall[],
    onComplete: (finalPos: { cx: number; cy: number }) => void,
  ): void {
    this.cleanupSimulation();
    const sim = createDropSimulation(ball.r, dropFromX, dropFromY, otherBalls);
    this.simulation = sim;

    let frame = 0;
    const maxFrames = 180;

    const step = () => {
      if (!this.simulation) {
        return;
      }

      const pos = stepSingleBallSimulation(sim);
      this.animatedBall.set({ type: ball.type, cx: pos.cx, cy: pos.cy, r: ball.r });
      this.cdr.detectChanges();
      frame++;

      if ((frame > 30 && pos.speed < 0.1) || frame >= maxFrames) {
        this.cleanupSimulation();
        onComplete({ cx: pos.cx, cy: pos.cy });
      } else {
        this.animationFrameId = requestAnimationFrame(step);
      }
    };

    this.animationFrameId = requestAnimationFrame(step);
  }

  private runPhysicsSettle(remaining: PositionedBall[]): void {
    this.cleanupSimulation();
    this.simulation = createSettleSimulation(remaining);

    let frame = 0;
    const maxFrames = 120;

    const step = () => {
      if (!this.simulation) {
        return;
      }

      const updated = stepSimulation(this.simulation, remaining);
      this.settledBalls.set(updated);
      this.cdr.detectChanges();
      frame++;

      if (frame < maxFrames && !isSettled(this.simulation)) {
        this.animationFrameId = requestAnimationFrame(step);
      } else {
        this.finishSettle();
      }
    };

    this.animationFrameId = requestAnimationFrame(step);
  }

  private finishSettle(): void {
    this.cleanupSimulation();
    this.isAnimating.set(false);
  }

  private cleanupSimulation(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = 0;
    }
    if (this.simulation) {
      destroySimulation(this.simulation);
      this.simulation = null;
    }
    if (this.concurrentSettleSim) {
      destroySimulation(this.concurrentSettleSim);
      this.concurrentSettleSim = null;
    }
  }
}
